import { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';

import { APP_RATING_POSTED_KEY, AMAZON_REVIEW_URL } from '../../app/constants/constants';
import { FIRST_ONBOARDING_BOOLEAN_UPDATE } from '../../app/graphql/mutations';
import { LAST_TWO_FIVE_STAR_SESSIONS } from '../../app/graphql/queries';
import { setUser, userIdSelector, userSelector } from '../../app/main/mainSlice';
import { useAppDispatch, useAppSelector } from '../../app/store/hooks';
import { getLocalStorage } from '../../utils/localStorageUtils';

const removeUndefinedValues = (obj: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== undefined));

const AmazonReviewPromptGate = () => {
  const userId = useAppSelector(userIdSelector);
  const user = useAppSelector(userSelector);
  const dispatch = useAppDispatch();
  const location = useLocation();

  const [showModal, setShowModal] = useState(false);
  const [getLastTwoFiveStarSessions] = useLazyQuery(LAST_TWO_FIVE_STAR_SESSIONS, {
    fetchPolicy: 'network-only',
  });
  const [updateUserAppSettingsMutation] = useMutation(FIRST_ONBOARDING_BOOLEAN_UPDATE);

  const alreadyPrompted = user?.userAppSettings?.amazonReviewPrompted === true;
  const playStorePromptShown = getLocalStorage(APP_RATING_POSTED_KEY) === 'true';
  const isOnHomepage = location.pathname === '/';

  const isEligible =
    !!userId &&
    !alreadyPrompted &&
    playStorePromptShown &&
    isOnHomepage;

  const updateUserAppSettings = async () => {
    if (!user?.id) return;

    const currentSettings = user?.userAppSettings ?? {};
    const nextSettings = removeUndefinedValues({
      ...currentSettings,
      amazonReviewPrompted: true,
    });

    try {
      const { data: mutationData } = await updateUserAppSettingsMutation({
        variables: { userId: user.id, userAppSettings: nextSettings },
      });

      const updatedSettings = mutationData?.update_users_by_pk?.userAppSettings;
      if (updatedSettings) {
        dispatch(
          setUser({
            user: {
              ...user,
              userAppSettings: updatedSettings,
            },
          })
        );
      }
    } catch (error) {
      // Silently fail so the prompt can be retried in a later session.
    }
  };

  const handleClose = () => {
    setShowModal(false);
    updateUserAppSettings();
  };

  const handleWriteReview = () => {
    window.open(AMAZON_REVIEW_URL, '_blank');
    handleClose();
  };

  useEffect(() => {
    const checkEligibility = async () => {
      if (isEligible && userId) {
        try {
          const { data } = await getLastTwoFiveStarSessions({
            variables: { userId },
          });

          const sessions = data?.Sessions_DB || [];
          const hasTwoFiveStarSessions = sessions.length >= 2;

          if (hasTwoFiveStarSessions) {
            setShowModal(true);
          }
        } catch (error) {
          // Silently fail so eligibility can be re-evaluated next time.
        }
      }
    };

    checkEligibility();
  }, [isEligible, userId, getLastTwoFiveStarSessions]);

  return (
    <Dialog open={showModal} onClose={handleClose}>
      <DialogTitle>Enjoying cooking with upliance? 😊</DialogTitle>
      <DialogContent>
        <Typography>
          Your review on Amazon helps other home cooks discover us and supports our small team.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Skip</Button>
        <Button onClick={handleWriteReview} variant="contained">
          Write a review
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AmazonReviewPromptGate;
